import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { 
  Globe, 
  FileText, 
  Download,
  Upload,
  CheckCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Calendar,
  Send,
  FileCheck,
  XCircle
} from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { ro } from 'date-fns/locale';

interface EurocatReport {
  report_id: number;
  period_start: string;
  period_end: string;
  status: 'draft' | 'validated' | 'submitted' | 'accepted' | 'rejected';
  total_cases: number;
  validated_cases: number;
  errors_count: number;
  warnings_count: number;
  submission_date: string | null;
  acceptance_date: string | null;
  created_at: string;
  created_by: string;
}

interface ValidationError {
  case_id: number;
  patient_name: string;
  error_type: 'missing_data' | 'invalid_format' | 'duplicate' | 'inconsistent';
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export const EurocatReports: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reports, setReports] = useState<EurocatReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<EurocatReport | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [dateRange, setDateRange] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: startOfMonth(subMonths(new Date(), 3)),
    to: endOfMonth(new Date())
  });
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setError(null);
      // For now, we'll use empty data since EUROCAT reports endpoint is not yet implemented
      // TODO: Replace with actual API call when endpoint is available
      // const response = await api.get('/api/reports/eurocat/history');
      setReports([]);
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading reports:', err);
      setError(err.message || 'Eroare la încărcarea rapoartelor');
      setLoading(false);
    }
  };

  const generateReport = async () => {
    setGeneratingReport(true);
    setError(null);
    try {
      // Simulate report generation
      setTimeout(() => {
        const newReport: EurocatReport = {
          report_id: reports.length + 1,
          period_start: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
          period_end: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : '',
          status: 'draft',
          total_cases: Math.floor(Math.random() * 50) + 100,
          validated_cases: 0,
          errors_count: 0,
          warnings_count: 0,
          submission_date: null,
          acceptance_date: null,
          created_at: format(new Date(), 'yyyy-MM-dd'),
          created_by: 'Current User'
        };
        setReports([newReport, ...reports]);
        setGeneratingReport(false);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Eroare la generarea raportului');
      setGeneratingReport(false);
    }
  };

  const validateReport = (report: EurocatReport) => {
    setSelectedReport(report);
    // Simulate loading validation errors
    setValidationErrors([
      {
        case_id: 1234,
        patient_name: 'Ion Popescu',
        error_type: 'missing_data',
        field: 'diagnosis_date',
        message: 'Data diagnosticului lipsește',
        severity: 'error'
      },
      {
        case_id: 1235,
        patient_name: 'Maria Ionescu',
        error_type: 'invalid_format',
        field: 'icd10_code',
        message: 'Cod ICD-10 invalid pentru MCC',
        severity: 'error'
      },
      {
        case_id: 1236,
        patient_name: 'Andrei Popa',
        error_type: 'inconsistent',
        field: 'birth_weight',
        message: 'Greutate la naștere pare incorectă pentru vârsta gestațională',
        severity: 'warning'
      }
    ]);
  };

  const submitReport = async (report: EurocatReport) => {
    try {
      setError(null);
      // Simulate API call
      const updatedReport = { ...report, status: 'submitted' as const, submission_date: format(new Date(), 'yyyy-MM-dd') };
      setReports(reports.map(r => r.report_id === report.report_id ? updatedReport : r));
    } catch (err: any) {
      setError(err.message || 'Eroare la trimiterea raportului');
    }
  };

  const exportReport = (report: EurocatReport, format: 'xml' | 'csv' | 'pdf') => {
    // Implement actual export logic
  };

  const getStatusBadge = (status: EurocatReport['status']) => {
    const config = {
      draft: { variant: 'secondary' as const, icon: FileText, label: 'Ciornă', className: '' },
      validated: { variant: 'default' as const, icon: FileCheck, label: 'Validat', className: '' },
      submitted: { variant: 'default' as const, icon: Send, label: 'Trimis', className: 'bg-blue-500' },
      accepted: { variant: 'default' as const, icon: CheckCircle, label: 'Acceptat', className: 'bg-green-500' },
      rejected: { variant: 'destructive' as const, icon: XCircle, label: 'Respins', className: '' }
    };
    const { variant, icon: Icon, label, className } = config[status];
    return (
      <Badge variant={variant} className={className}>
        <Icon className="mr-1 h-3 w-3" />
        {label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Rapoarte EUROCAT</h1>
            <p className="text-muted-foreground">
              Generează și trimite rapoarte către registrul EUROCAT
            </p>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rapoarte trimise</CardTitle>
              <Send className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{reports.filter(r => r.status === 'submitted' || r.status === 'accepted').length}</div>
              <p className="text-xs text-muted-foreground">În ultimele 12 luni</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Cazuri raportate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {reports.reduce((sum, r) => sum + r.validated_cases, 0)}
              </div>
              <p className="text-xs text-muted-foreground">Total cazuri validate</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rată acceptare</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">95%</div>
              <p className="text-xs text-muted-foreground">Rapoarte acceptate</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Următorul termen</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">15 Ian</div>
              <p className="text-xs text-muted-foreground">2025 Q4 Report</p>
            </CardContent>
          </Card>
        </div>

        {/* Generate New Report */}
        <Card>
          <CardHeader>
            <CardTitle>Generează raport nou</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Perioada de raportare</label>
                <DatePickerWithRange
                  date={dateRange}
                  onDateChange={(range) => {
                    if (range) {
                      setDateRange({ from: range.from, to: range.to });
                    }
                  }}
                />
              </div>
              <Select defaultValue="all">
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Selectează spitalul" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate spitalele</SelectItem>
                  <SelectItem value="hospital1">Spital 1</SelectItem>
                  <SelectItem value="hospital2">Spital 2</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={generateReport} disabled={generatingReport}>
                {generatingReport ? (
                  <>
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    Generare...
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    Generează raport
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Reports Table */}
        <Card>
          <CardHeader>
            <CardTitle>Istoric rapoarte</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Perioadă</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Cazuri</TableHead>
                  <TableHead>Validare</TableHead>
                  <TableHead>Data trimiterii</TableHead>
                  <TableHead>Data acceptării</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.report_id}>
                    <TableCell className="font-medium">
                      {format(new Date(report.period_start), 'dd MMM yyyy', { locale: ro })} -{' '}
                      {format(new Date(report.period_end), 'dd MMM yyyy', { locale: ro })}
                    </TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{report.validated_cases}/{report.total_cases}</span>
                        <Progress value={(report.validated_cases / report.total_cases) * 100} className="h-1 mt-1" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {report.errors_count > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            {report.errors_count} erori
                          </Badge>
                        )}
                        {report.warnings_count > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {report.warnings_count} avertismente
                          </Badge>
                        )}
                        {report.errors_count === 0 && report.warnings_count === 0 && (
                          <Badge variant="default" className="text-xs bg-green-500">
                            Valid
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {report.submission_date ? 
                        format(new Date(report.submission_date), 'dd MMM yyyy', { locale: ro }) : 
                        '-'
                      }
                    </TableCell>
                    <TableCell>
                      {report.acceptance_date ? 
                        format(new Date(report.acceptance_date), 'dd MMM yyyy', { locale: ro }) : 
                        '-'
                      }
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => validateReport(report)}
                          aria-label="Validează raportul"
                        >
                          <FileCheck className="h-4 w-4" />
                        </Button>
                        {report.status === 'validated' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => submitReport(report)}
                            aria-label="Trimite raportul"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => exportReport(report, 'xml')}
                          aria-label="Descarcă raportul (XML)"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Validation Errors Panel */}
        {selectedReport && validationErrors.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Erori de validare - Raport {format(new Date(selectedReport.period_start), 'MMM yyyy', { locale: ro })}</span>
                <Button variant="outline" size="sm" onClick={() => setSelectedReport(null)}>
                  Închide
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Caz ID</TableHead>
                    <TableHead>Pacient</TableHead>
                    <TableHead>Tip eroare</TableHead>
                    <TableHead>Câmp</TableHead>
                    <TableHead>Mesaj</TableHead>
                    <TableHead>Severitate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {validationErrors.map((error, index) => (
                    <TableRow key={index}>
                      <TableCell>{error.case_id}</TableCell>
                      <TableCell>{error.patient_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {error.error_type.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{error.field}</TableCell>
                      <TableCell>{error.message}</TableCell>
                      <TableCell>
                        <Badge variant={error.severity === 'error' ? 'destructive' : 'secondary'}>
                          {error.severity === 'error' ? 'Eroare' : 'Avertisment'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};